// Campaigns and the assistant's tenant settings (docs/contract.md, section 15). Shapes mirror
// crates/api-types/src/campaigns.rs.

import { queryOptions } from "@tanstack/react-query";
import type { TagPickerValue } from "@vd/ui";
import { api } from "./client";

/** `pending`: a private sub-campaign's tag, made in Odoo when its owner shares it. */
export type TagState = "none" | "pending" | "ok" | "failed";

export interface CampaignNode {
  id: string;
  parent_id: string | null;
  root_id: string;
  depth: number;
  name: string;
  /** The brief's one line (at most 200 characters); empty until written. */
  summary?: string;
  /** The brief's document (Markdown). */
  brief_md: string;
  /** The brief's latest version; sent back with an edit. */
  brief_version?: number;
  owner_user_id: string | null;
  owner_name: string | null;
  /** The owner, else the admin who made it (avatar and tooltip). */
  owner?: { user_id: string; name: string; photo_version?: string } | null;
  mine: boolean;
  /** `owner` Only you, `team` the owner's team, `members` everyone in the top-level campaign. */
  visibility: "members" | "team" | "owner";
  allow_user_subs: boolean;
  tag: { state: TagState; crm_id: string | null; name: string | null; error: string | null };
  /** The tag it gets by default: "Campaign / Hyperprints / Nebraska". */
  tag_default: string;
  status: "active" | "archived";
  list_count: number;
  pending_suggestions: number;
  ai: { active: boolean; paused_by: { pause_id: string; until: string | null } | null };
  children: CampaignNode[];
  created_at: string;
  updated_at: string;
}

export interface CampaignMember {
  user_id: string;
  name: string;
  email: string;
  role: "lead" | "member";
}

export interface CampaignDetail {
  campaign: CampaignNode;
  path: { id: string; name: string }[];
  members: CampaignMember[];
  lists: { id: string; name: string; owner_name: string | null; total: number; done: number }[];
  stats_30d: {
    calls: number;
    connected: number;
    voicemails: number;
    opportunities_created: number;
    follow_ups_booked: number;
  };
  can_edit: boolean;
  can_add_sub: boolean;
  /** The caller may change its Odoo tag. */
  can_tag: boolean;
  /** Add and remove people: admins, and managers who lead it. */
  can_manage_members?: boolean;
  /** People in the top-level campaign (sub-campaigns share their root's members). */
  member_count: number;
}

/**
 * How a campaign gets its Odoo tag. `custom` makes the tag `name` in `category` (top level
 * without one). Ids travel to the server only; screens show the pill and its category.
 */
export type TagChoice = TagPickerValue;

export interface TagSearch {
  items: { crm_id: string; name: string }[];
  campaign_namespace: string;
  /** The caller's Odoo user may create tags; `false` only when Odoo said no. */
  can_create: boolean;
}

export interface NewCampaign {
  parent_id?: string | null;
  name: string;
  summary?: string;
  brief_md?: string;
  allow_user_subs?: boolean;
  tag?: TagChoice;
  members?: string[];
  visibility?: "members" | "team" | "owner";
}

export interface Instruction {
  body_md: string;
  version: number;
  max_chars: number;
  updated_at: string | null;
  updated_by_name: string | null;
}

export interface InstructionVersion {
  version: number;
  body_md: string;
  updated_at: string;
  updated_by_name: string | null;
}

export interface PromptPreview {
  prompt: string;
  prompt_version: string;
  sections: { name: string; chars: number; trimmed: boolean }[];
  total_chars: number;
}

export interface AgentSettings {
  enabled: boolean;
  background: boolean;
  review_time: string;
  review_days: number[];
  daily_usd_per_user: number;
  monthly_usd_tenant: number;
  models: { chat: string; voice: string; digest: string; plan: string; research: string; mapping: string };
  imports_max_rows: number;
  imports_rollback_days: number;
  research_max_candidates_per_run: number;
  research_runs_per_user_per_day: number;
  research_max_usd_per_run?: number;
  research_sources?: { web: boolean; places: boolean; browse: boolean };
  tag_namespaces: { campaign: string; imports: string; source: string };
  /** Performance reviews (ADR 0009). Absent on older servers: the defaults apply. */
  perf_review_enabled?: boolean;
  perf_review_reps_can_run?: boolean;
  perf_review_schedule?: { daily: boolean; weekly: boolean; monthly: boolean };
  perf_review_max_usd_per_run?: number;
  perf_review_runs_per_rep_per_day?: number;
  perf_review_scheduled_max_usd_month?: number;
  /** Spoken "Try this" examples in reviews (ADR 0028). Absent on older servers: on. */
  perf_review_audio?: boolean;
  /** The most one Assistant message may spend on lookups before it answers (default $1). */
  turn_max_usd?: number;
  /** The agent engine (ADR 0021), in preview. Absent from servers that predate it. */
  engine?: EngineSettings;
  /** The model router (ADR 0063). Absent from servers that predate it. */
  router?: RouterSettings;
}

/** `current` until the owner approves the eval defaults; an admin picks one of the other three. */
export type QualityPolicy = "economy" | "balanced" | "best" | "current";

export type TaskClass =
  | "tool_round"
  | "plan"
  | "answer"
  | "summarize"
  | "classify"
  | "draft"
  | "design"
  | "synthesize";

/** A company's own model choice for one vendor: Kite's router, one model for everything, or one per task. */
export interface OwnModelChoice {
  all?: string | null;
  classes?: Partial<Record<TaskClass, string>>;
  /** Kite's router on this vendor's models and the company's key: Balanced, Best or Economy. */
  router?: Exclude<QualityPolicy, "current"> | null;
}

export interface RouterSettings {
  policy: QualityPolicy;
  own_models?: {
    anthropic?: OwnModelChoice | null;
    xai?: OwnModelChoice | null;
    openai?: OwnModelChoice | null;
  };
}

/** The models each vendor's price book lists: the only ones the picker offers. */
export interface PricedModels {
  anthropic: string[];
  openai: string[];
  xai: string[];
}

/** Which Assistant engine reps get, and the new engine's models and per-message ceiling. */
export interface EngineSettings {
  /** What reps get unless they are listed in `users`. */
  default: "legacy" | "agent";
  /** Members who get the new engine whatever the default. */
  users: string[];
  chat_model: string;
  work_model: string;
  turn_max_usd: number;
}

export const ENGINE_DEFAULTS: EngineSettings = {
  default: "legacy",
  users: [],
  chat_model: "claude-sonnet-5-5",
  work_model: "claude-opus-5-5",
  turn_max_usd: 3,
};

export interface AssistantUsage {
  month_usd: number;
  monthly_cap_usd: number;
  daily_cap_usd: number;
  reps: { user_id: string; name: string; today_usd: number; month_usd: number; model_calls_today: number }[];
  by_task: { task: string; usd: number }[];
  /** The company per month, newest first (six months). Absent from older servers. */
  months?: MonthSpend[];
  /** Each rep per month over the same months. */
  rep_months?: (MonthSpend & { user_id: string; name: string })[];
}

/** One month of Assistant spend (ADR 0018). */
export interface MonthSpend {
  /** "2026-10". */
  month: string;
  usd: number;
  /** Typed Assistant turns. */
  turns: number;
  model_calls: number;
  tool_calls: number;
  input_tokens: number;
  cached_input_tokens: number;
  output_tokens: number;
}

/** "Oct 2026" for "2026-10". */
export function monthLabel(m: string): string {
  const [y, mo] = m.split("-").map(Number);
  if (!y || !mo) return m;
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Of the input tokens, the share read from the cache ("62%"). */
export function cachedShare(m: Pick<MonthSpend, "input_tokens" | "cached_input_tokens">): string {
  return m.input_tokens > 0 ? `${Math.round((m.cached_input_tokens / m.input_tokens) * 100)}%` : "0%";
}

/** The company's starter template for new briefs (story T1); the product default without one. */
export interface BriefTemplate {
  body_md: string;
  custom: boolean;
  default_md: string;
  updated_at: string | null;
  updated_by_name: string | null;
}

export const ck = {
  briefTemplate: ["campaigns", "brief-template"] as const,
  tree: ["admin", "campaigns"] as const,
  detail: (id: string) => ["admin", "campaign", id] as const,
  fundamentals: ["admin", "assistant", "fundamentals"] as const,
  versions: ["admin", "assistant", "versions"] as const,
  settings: ["admin", "assistant", "settings"] as const,
  usage: ["admin", "assistant", "usage"] as const,
};

export const campaignsApi = {
  briefTemplate: () => api<BriefTemplate>("/v1/brief-template"),
  saveBriefTemplate: (body_md: string) =>
    api<BriefTemplate>("/v1/admin/brief-template", { method: "PUT", body: { body_md } }),
  resetBriefTemplate: () => api<BriefTemplate>("/v1/admin/brief-template", { method: "DELETE" }),
  tree: () => api<{ items: CampaignNode[]; can_create?: boolean }>("/v1/admin/campaigns"),
  detail: (id: string) => api<CampaignDetail>(`/v1/admin/campaigns/${id}`),
  create: (body: NewCampaign) => api<CampaignDetail>("/v1/admin/campaigns", { method: "POST", body }),
  update: (
    id: string,
    body: Partial<{
      name: string;
      summary: string;
      brief_md: string;
      /** The version the edit started from: 409 `brief_conflict` when it moved on. */
      brief_version: number;
      status: "active" | "archived";
      allow_user_subs: boolean;
    }>,
  ) => api<CampaignDetail>(`/v1/admin/campaigns/${id}`, { method: "PATCH", body }),
  addMember: (id: string, user_id: string) =>
    api<CampaignDetail>(`/v1/admin/campaigns/${id}/members`, {
      method: "PUT",
      body: { user_id, role: "member" },
    }),
  removeMember: (id: string, userId: string) =>
    api<void>(`/v1/admin/campaigns/${id}/members/${userId}`, { method: "DELETE" }),
  /** Adds and removes members in one step. */
  patchMembers: (id: string, body: { add: string[]; remove: string[] }) =>
    api<CampaignDetail>(`/v1/admin/campaigns/${id}/members`, { method: "PATCH", body }),
  /** Odoo tags whose path contains `q`, best first. */
  tags: (q: string, signal?: AbortSignal) =>
    api<TagSearch>(`/v1/campaigns/tags?q=${encodeURIComponent(q)}`, signal ? { signal } : {}),
  /** Pause suggestions in a campaign for every rep. */
  pause: (campaign_id: string) =>
    api<{ id: string }>("/v1/ai/pauses", {
      method: "POST",
      body: { scope: "campaign", campaign_id, everyone: true },
    }),
  resume: (pause_id: string) => api<void>(`/v1/ai/pauses/${pause_id}`, { method: "DELETE" }),
  retryTag: (id: string, tag?: TagChoice) =>
    api<CampaignDetail>(`/v1/campaigns/${id}/tag`, { method: "POST", body: tag ? { tag } : {} }),
  fundamentals: () => api<Instruction>("/v1/admin/assistant/fundamentals"),
  saveFundamentals: (body: { body_md: string; version: number }) =>
    api<Instruction>("/v1/admin/assistant/fundamentals", { method: "PUT", body }),
  versions: () => api<{ items: InstructionVersion[] }>("/v1/admin/assistant/fundamentals/versions"),
  preview: (user_id: string, campaign_id: string | null) =>
    api<PromptPreview>("/v1/admin/assistant/prompt-preview", {
      method: "POST",
      body: { user_id, campaign_id },
    }),
  settings: () => api<{ settings: AgentSettings; updated_at: string | null }>("/v1/admin/assistant/settings"),
  saveSettings: (s: AgentSettings) =>
    api<{ settings: AgentSettings; updated_at: string | null }>("/v1/admin/assistant/settings", {
      method: "PUT",
      body: s,
    }),
  usage: () => api<AssistantUsage>("/v1/admin/assistant/usage"),
  pricedModels: () => api<PricedModels>("/v1/admin/assistant/models"),
};

export const campaignTreeQuery = queryOptions({ queryKey: ck.tree, queryFn: campaignsApi.tree });
export const campaignQuery = (id: string) =>
  queryOptions({ queryKey: ck.detail(id), queryFn: () => campaignsApi.detail(id) });
export const fundamentalsQuery = queryOptions({
  queryKey: ck.fundamentals,
  queryFn: campaignsApi.fundamentals,
});
export const assistantSettingsQuery = queryOptions({
  queryKey: ck.settings,
  queryFn: campaignsApi.settings,
});
export const assistantUsageQuery = queryOptions({
  queryKey: ck.usage,
  queryFn: campaignsApi.usage,
  refetchInterval: 60_000,
});

/** "$1.23", or "$0.004" below a cent. */
export function usd(n: number): string {
  if (n > 0 && n < 0.01) return `$${n.toFixed(3)}`;
  const s = n.toFixed(2);
  // Never "$-0.00".
  return Number(s) === 0 ? "$0.00" : n < 0 ? `-$${s.slice(1)}` : `$${s}`;
}
